import { lazy, Suspense, type ReactNode } from 'react';
import { OpenFeature } from '@openfeature/react-sdk';
import { getAnalysisForDownload, statistaDatasetsQuery } from '@/entities/analysis';
import type { Analysis } from '@/entities/analysis';
import { getContentTestForDownload } from '@/entities/content-test';
import { getOpportunityById } from '@/entities/opportunity';
import type { OpportunityCardItem } from '@/entities/opportunity';
import { getSinglePersona, personasByIdsQuery } from '@/entities/persona';
import type { Persona } from '@/entities/persona';
import { getSingleTerritory } from '@/features/strategic-territories-widget';
import {
  getSingleCampaign,
  normalizeWidgetData,
  toAdditionalImages,
  toMainImage,
  toSummary,
} from '@/features/ideation-widget';
import type { IdeationWidgetDataUnion } from '@/features/ideation-widget';
import { getAudienceSessionSnapshotForRender } from '@/features/audience-interview';
import { getFocusGroupForDownload } from '@/features/focus-group';
import { ChatTranscriptPreview, type ChatTranscriptMessage } from '@/shared/ui';
import type { AudienceSessionWidget, Territory } from '@inference.engine/widgets/web';
import { useStartInterview } from '@/features/audiences';
import type { SocialMediaOpportunitiesDeps } from '@/features/social-media-opportunities-widget';
import { OpportunityDetail } from '@/features/social-media-opportunities-widget/ui/components/OpportunityDetail/OpportunityDetail';
import { queryClient } from '@/lib/config';
import { ROUTE_PATTERNS } from '@/shared/config/routes';
import type { ContentTestDto, FocusGroupResponseDto } from '@inference.engine/persona-api-client';

// Dynamic import: keeps Recharts out of the /download chunk, and a static
// `pages/Download` → `pages/ContentTest` import would trip the FSD boundary rule.
const loadResultsReport = () =>
  import('@/pages/ContentTest/components/ResultsReport').then((m) => ({
    default: m.ResultsReport,
  }));

const ResultsReport = lazy(loadResultsReport);

const loadFocusGroupReportContent = () =>
  import('@/features/focus-group/components/FocusGroupReport/FocusGroupReportContent').then(
    (m) => ({
      default: m.FocusGroupReportContent,
    }),
  );

const FocusGroupReportContent = lazy(loadFocusGroupReportContent);

const loadPersonaDetailV2 = () =>
  import('@/features/audience-flow-widget').then((m) => ({ default: m.PersonaDetailV2 }));

const PersonaDetailV2 = lazy(loadPersonaDetailV2);

const loadTerritoryDetail = () =>
  import('@/features/strategic-territories-widget').then((m) => ({ default: m.TerritoryDetail }));

const TerritoryDetail = lazy(loadTerritoryDetail);

const loadAnalysisSectionExport = () =>
  import('@/pages/AnalysisResult').then((m) => ({ default: m.AnalysisSectionExport }));

const AnalysisSectionExport = lazy(loadAnalysisSectionExport);
const loadIdeaListPanel = () =>
  import('@/features/ideation-widget').then((m) => ({ default: m.IdeaListPanel }));

const IdeaListPanel = lazy(loadIdeaListPanel);

const loadIdeaDetailPanel = () =>
  import('@/features/ideation-widget').then((m) => ({ default: m.IdeaDetailPanel }));

const IdeaDetailPanel = lazy(loadIdeaDetailPanel);

// Preload chunks in parallel with the page's data query.
void loadResultsReport();
void loadFocusGroupReportContent();
void loadPersonaDetailV2();
void loadTerritoryDetail();
void loadAnalysisSectionExport();
void loadIdeaListPanel();
void loadIdeaDetailPanel();

/**
 * Registry of downloadable document types for the `/download` route (see
 * `Download.tsx`). Keys mirror the backend `DownloadableDocumentType`. Each entry:
 *   - `fetch`:     loads the document by (workspaceId, id); driven by `useQuery`.
 *   - `useRender`: a hook returning `(data, opts?) => ReactNode`; runs at the
 *                  page's top level so it may call hooks.
 */
/**
 * Optional per-document hints from the `/download` URL, passed to both `fetch`
 * and `useRender`. `section` picks a part of a document (focus-group);
 * `itemIndex` picks one item from a collection-backed document (territory).
 */
interface DownloadOptions {
  section?: string;
  itemIndex?: number;
}

export interface Downloadable<TData = unknown> {
  fetch: (workspaceId: string, id: string, opts?: DownloadOptions) => Promise<TData>;
  useRender: () => (data: TData, opts?: DownloadOptions) => ReactNode;
}

const opportunityDownloadable: Downloadable<OpportunityCardItem> = {
  fetch: (workspaceId, id) => getOpportunityById(workspaceId, id),
  useRender: () => {
    // Mirror the room's deps wiring so the SparkCard "start interview" CTA resolves
    // (it is a no-op in readOnly/exporting mode, but the dep must be the right shape).
    const detailDeps = {
      useStartInterview: () =>
        useStartInterview() as unknown as ReturnType<
          SocialMediaOpportunitiesDeps['useStartInterview']
        >,
    };
    // `readOnly` hides the in-content close control; `exporting` renders the
    // fully-expanded export variant.
    return (data) => <OpportunityDetail item={data} deps={detailDeps} readOnly exporting />;
  },
};

const contentTestDownloadable: Downloadable<ContentTestDto> = {
  fetch: (workspaceId, id) => getContentTestForDownload(workspaceId, id),
  // `exporting` expands every section and hides interactive chrome for a complete
  // static capture. The fallback must be `null`, not a spinner: a spinner would
  // signal "ready" to the headless renderer before the report has mounted.
  useRender: () => (data) => (
    <Suspense fallback={null}>
      <ResultsReport test={data} readOnly exporting />
    </Suspense>
  ),
};

const strategicTerritoryDownloadable: Downloadable<Territory> = {
  // `itemIndex` selects one territory from the artifact's collection (default 0).
  fetch: (workspaceId, id, opts) =>
    getSingleTerritory(id, { workspaceId, itemIndex: opts?.itemIndex ?? 0 }),
  // `exporting` drops the side-panel scroll effect and prev/next navigation.
  useRender: () => (data) => (
    <Suspense fallback={null}>
      <TerritoryDetail territory={data} exporting onPrevious={() => {}} onNext={() => {}} />
    </Suspense>
  ),
};

const ideationDownloadable: Downloadable<IdeationWidgetDataUnion> = {
  // Fetch the whole campaign either way; the idea detail needs the campaign's
  // summary + references for context. `itemIndex` selects the view below, and is
  // validated here so an out-of-range/non-numeric index errors instead of
  // silently exporting the overview.
  fetch: (workspaceId, id, opts) =>
    getSingleCampaign(id, { workspaceId, itemIndex: opts?.itemIndex }),
  // No `itemIndex` -> campaign overview (ideas as a title-only list). With one
  // -> that idea's detail. Null fallback (see content-test).
  useRender: () => (raw, opts) => {
    const data = normalizeWidgetData(raw);
    const summary = toSummary(data);
    const idea = opts?.itemIndex != null ? data.ideas[opts.itemIndex] : undefined;
    return (
      <Suspense fallback={null}>
        {idea ? (
          <IdeaDetailPanel
            idea={idea}
            ideaIndex={opts?.itemIndex ?? 0}
            summary={summary}
            benchmarkReferences={data.references}
            onBack={() => {}}
            onDescriptionSave={() => {}}
            exporting
          />
        ) : (
          <IdeaListPanel
            ideas={data.ideas}
            summary={summary}
            benchmarkReferences={data.references}
            campaignTitle={data.title}
            mainImage={toMainImage(data)}
            additionalImages={toAdditionalImages(data)}
            onIdeaSelect={() => {}}
            exporting
          />
        )}
      </Suspense>
    );
  },
};
const audienceSessionDownloadable: Downloadable<AudienceSessionWidget | null> = {
  // `id` is the session contextId; the snapshot is built live server-side, so
  // the export renders without persisting an artifact.
  fetch: (workspaceId, id) => getAudienceSessionSnapshotForRender(workspaceId, id),
  // `exporting` lays the transcript out at full height (no scroll container) so
  // the headless capture gets every message. A null snapshot (invalid/missing)
  // is handled upstream by `Download.tsx`, which shows "Document not found".
  useRender: () => (snapshot) => {
    if (!snapshot) return null;
    const personaNameById = new Map(snapshot.personas.map((p) => [p.id, p.name]));
    const messages: ChatTranscriptMessage[] = snapshot.messages.map((m) => ({
      role: m.role,
      content: m.content,
      speakerName:
        m.speakerName ?? (m.personaId ? (personaNameById.get(m.personaId) ?? null) : null),
      createdAt: m.createdAt,
      isHandoff: m.isHandoff,
    }));
    return <ChatTranscriptPreview title={snapshot.title} messages={messages} exporting />;
  },
};

const personaDownloadable: Downloadable<Persona> = {
  fetch: (workspaceId, id) => getSinglePersona(id, { workspaceId }),
  // `exporting` hides the live-UI export button so it never prints into the PDF.
  // Null fallback (see content-test above).
  useRender: () => (data) => (
    <Suspense fallback={null}>
      <PersonaDetailV2 persona={data} exporting />
    </Suspense>
  ),
};

const focusGroupDownloadable: Downloadable<FocusGroupResponseDto> = {
  fetch: (workspaceId, id) => getFocusGroupForDownload(workspaceId, id),
  // One section per file (`?section=`) — the interviews transcripts alone exceed
  // Chromium's single-page PDF height cap. Null fallback (see content-test above).
  useRender: () => (data, opts) => (
    <Suspense fallback={null}>
      <FocusGroupReportContent
        focusGroup={data}
        exporting
        section={opts?.section === 'interviews' ? 'interviews' : 'summary'}
      />
    </Suspense>
  ),
};

const analysisDownloadable: Downloadable<Analysis> = {
  // Two sections render from a SECOND query the readiness signal can't see, so each is
  // prefetched here into the same cache key: it then resolves synchronously at render and
  // `__PDF_READY__` waits on this fetch rather than on a spinner. Both are gated on
  // `section` so a wasted request never blocks the other sections' exports.
  //
  // Core Personas (`personasByIdsQuery`) is Audience Profiles only. The Statista charts
  // (`statistaDatasetsQuery`) are Category & Competition only, and are additionally gated on
  // their flag: this runs outside React, so the flag is read off the client rather than
  // through `useIqStatistaChartsEnabled`.
  fetch: async (workspaceId, id, opts) => {
    const analysis = await getAnalysisForDownload(workspaceId, id);
    const personaIds = analysis.personaIds ?? [];
    if (opts?.section === ROUTE_PATTERNS.IQ_SECTION_AUDIENCE_PROFILES && personaIds.length) {
      await queryClient.prefetchQuery(personasByIdsQuery(workspaceId, personaIds));
    }
    if (
      opts?.section === ROUTE_PATTERNS.IQ_SECTION_CATEGORY_COMPETITION &&
      OpenFeature.getClient().getBooleanValue('iq_statista_charts', false)
    ) {
      await queryClient.prefetchQuery(statistaDatasetsQuery(id));
    }
    return analysis;
  },
  // One section per file (`?section=`): the IQ report is eight sections and each
  // has its own download button. `exporting` (inside AnalysisSectionExport)
  // suppresses that button so it never prints. Null fallback (see content-test above).
  useRender: () => (data, opts) => (
    <Suspense fallback={null}>
      <AnalysisSectionExport analysis={data} section={opts?.section} />
    </Suspense>
  ),
};

// Each entry is validated at its own declaration, so the map needs no annotation:
// a `Record<string, Downloadable<unknown>>` would fail on `data` contravariance.
// Inference keeps the literal keys (so `DownloadableType` narrows) and each entry's
// data type; `Download.tsx` only pairs an entry's fetch with its own render.
export const DOWNLOADABLES = {
  'audience-session': audienceSessionDownloadable,
  'content-test': contentTestDownloadable,
  'focus-group': focusGroupDownloadable,
  'iq-analysis': analysisDownloadable,
  'strategic-territory': strategicTerritoryDownloadable,
  ideation: ideationDownloadable,
  opportunity: opportunityDownloadable,
  persona: personaDownloadable,
};

export type DownloadableType = keyof typeof DOWNLOADABLES;

export const isDownloadableType = (value: string | undefined): value is DownloadableType =>
  value != null && Object.prototype.hasOwnProperty.call(DOWNLOADABLES, value);

export const getDownloadable = (type: DownloadableType): Downloadable<unknown> =>
  DOWNLOADABLES[type] as Downloadable<unknown>;
